import type { AggregateRating, Brand, CampgroundLeaf, CompoundPriceSpecification, GeoCoordinates, ImageObject, LocationFeatureSpecification, OpeningHoursSpecification, Person, Place, PostalAddress, PriceSpecification, PropertyValue, Rating, Review, TouristAttractionLeaf, UnitPriceSpecification, Vehicle } from 'schema-dts'
import type { COUNTRY_SHORTS } from '~/constants'
import { RV_MANUFACTURERS_NOT_APPLICABLE, RV_MANUFACTURERS_OTHER } from '~/constants/manufacturers'
import type { RvExpandedDetails } from '~/types/rv'

export default function useAugmentedSchemaOrg() {
  const runtimeConfig = useRuntimeConfig()
  const { formatAsDateISO } = useDates()

  const { t } = useI18n({
    useScope: 'global',
    messages: {
      en: {
        augmentedSchemaOrg: {
          none: 'None',
          unlimitedMileage: 'Unlimited Mileage',
          generator: 'Generator',
          length: 'Length',
          slideouts: 'Slide outs',
        },
      },
      fr: {
        augmentedSchemaOrg: {
          none: 'Aucun',
          unlimitedMileage: 'Kilométrage illimité',
          generator: 'Génératrice',
          length: 'Longueur',
          slideouts: 'Extensions coulissantes',
        },
      },
    },
  })

  const BASE_URL = runtimeConfig.public.baseURL

  function definePostalAddress({ streetAddress, city, region, countryCode, postalCode }: { streetAddress?: string, city?: string, region?: string, countryCode?: string, postalCode?: string }): PostalAddress | undefined {
    if (!countryCode) {
      return undefined
    }

    return {
      '@type': 'PostalAddress',
      '@id': `${BASE_URL}/#postal-address-${[streetAddress, city, region, countryCode].filter(Boolean).join('-')}`,
      'streetAddress': streetAddress,
      'addressLocality': city,
      'addressRegion': region,
      'addressCountry': countryCode,
      'postalCode': postalCode,
    }
  }

  function defineContainedInPlace({ city, region, countryCode }: { city?: string, region?: string, countryCode?: string }): Place | undefined {
    let schema: Place | undefined = undefined

    if (countryCode) {
      schema = {
        '@type': 'Country',
        '@id': `${BASE_URL}/#country-${countryCode}`,
        'name': countryCode,
      }
    }
    if (region) {
      schema = {
        '@type': 'AdministrativeArea',
        '@id': `${BASE_URL}/#region-${region}`,
        'name': region,
        ...(schema ? { containedInPlace: schema } : {}),
      }
    }

    if (city) {
      schema = {
        '@type': 'City',
        '@id': `${BASE_URL}/#city-${formatAsSlug(city)}`,
        'name': city,
        ...(schema ? { containedInPlace: schema } : {}),
      }
    }

    return schema
  }

  type LocationFeatureSpecificationObject = Omit<LocationFeatureSpecification, '@type'>
  type LocationFeatureSpecificationArray = LocationFeatureSpecificationObject[]
  function defineLocationFeatureSpecification(feature: LocationFeatureSpecificationObject): LocationFeatureSpecification {
    return {
      '@type': 'LocationFeatureSpecification',
      ...feature,
      'value': feature.value ?? true,
    }
  }

  function defineBrand({ brand }: { brand?: string }): Brand | undefined {
    if (!brand) {
      return undefined
    }

    return {
      '@type': 'Brand',
      '@id': `${BASE_URL}/#brand-${formatAsSlug(brand)}`,
      'name': brand,
    }
  }

  function defineGeoCoordinates({ latitude, longitude }: { latitude?: number, longitude?: number }): GeoCoordinates | undefined {
    if (typeof latitude !== 'number' || typeof longitude !== 'number') {
      return undefined
    }

    return {
      '@type': 'GeoCoordinates',
      'latitude': latitude,
      'longitude': longitude,
    }
  }

  function defineImageObject({ image }: { image?: string }): ImageObject | undefined {
    if (!image) {
      return undefined
    }
    return {
      '@type': 'ImageObject',
      'url': image,
    }
  }

  /** Calendar dates only; use UTC so date-only values are not shifted by the viewer timezone. */
  function defineOpeningHoursSpecification({ validFrom, validThrough }: { validFrom?: DatesParam, validThrough?: DatesParam }): OpeningHoursSpecification | undefined {
    const from = formatAsDateISO(validFrom, { timezone: 'UTC' })
    const through = formatAsDateISO(validThrough, { timezone: 'UTC' })
    if (!from || !through) {
      return undefined
    }

    return {
      '@type': 'OpeningHoursSpecification',
      'validFrom': from,
      'validThrough': through,
    }
  }

  function definePerson({ name, image, url }: { name?: string, image?: string, url?: string }): Person | undefined {
    if (!name) {
      return undefined
    }

    return {
      '@type': 'Person',
      'name': name,
      'image': image || undefined,
      'url': rewriteAuthorUrl(url),
    }
  }

  type ReviewObject = { authorName?: string, authorImage?: string, authorUrl?: string, datePublished?: string, reviewBody: string, ratingValue: number }
  function defineReview({ authorName, authorImage, authorUrl, datePublished, reviewBody, ratingValue }: ReviewObject): Review {
    return {
      '@type': 'Review',
      'author': definePerson({ name: authorName, image: authorImage, url: authorUrl }),
      'datePublished': formatAsDateISO(datePublished),
      'reviewBody': reviewBody,
      'reviewRating': defineRating({ ratingValue }),
    }
  }

  function definePropertyValue({ name, value, unitCode }: Omit<PropertyValue, '@type'>): PropertyValue {
    return {
      '@type': 'PropertyValue',
      'name': name,
      'value': value,
      'unitCode': unitCode ?? undefined,
    }
  }

  function defineRating({ ratingValue }: { ratingValue: number }): Rating | undefined {
    if (!ratingValue) {
      return undefined
    }

    return {
      '@type': 'Rating',
      'ratingValue': formatWithPrecision(ratingValue, 1),
      'bestRating': 5,
    }
  }

  function defineAggregateRating({ ratingValue, reviewCount }: { ratingValue: number, reviewCount: number }): AggregateRating {
    return {
      '@type': 'AggregateRating',
      'ratingValue': formatWithPrecision(ratingValue, 1),
      'reviewCount': reviewCount,
      'bestRating': 5,
    }
  }

  function defineTouristAttraction({ id = '#attraction', name, image, description, url, latitude, longitude, city, region, countryCode, amenityFeature }: { id?: string, name: string, image?: string, description?: string, url?: string, latitude?: number, longitude?: number, city?: string, region?: string, countryCode?: string, amenityFeature?: LocationFeatureSpecificationArray }): TouristAttractionLeaf {
    return {
      '@id': `${BASE_URL}/${id}`,
      '@type': 'TouristAttraction',
      'name': name,
      'image': image || undefined,
      'description': description || undefined,
      'url': url || undefined,
      'geo': defineGeoCoordinates({ latitude, longitude }),
      'address': definePostalAddress({ city, region, countryCode }),
      'containedInPlace': defineContainedInPlace({ city, region, countryCode }),
      'amenityFeature': amenityFeature?.map(defineLocationFeatureSpecification) ?? undefined,
    } satisfies TouristAttractionLeaf
  }

  function defineCampground({
    id = '#campground',
    name,
    telephone,
    url,
    petsAllowed,
    numberOfRooms,
    priceRange,
    brand,
    image,
    description,
    latitude,
    longitude,
    streetAddress,
    city,
    region,
    countryCode,
    postalCode,
    seasonStarts,
    seasonEnds,
    amenityFeature,
    reviews,
    aggregateRating,
  }: {
    id?: string
    name: string
    telephone?: string
    url?: string
    petsAllowed?: boolean
    numberOfRooms?: number
    priceRange?: string
    brand?: string
    image?: string
    description?: string
    latitude?: number
    longitude?: number
    streetAddress?: string
    city?: string
    region?: string
    countryCode?: string
    postalCode?: string
    seasonStarts?: string
    seasonEnds?: string
    amenityFeature?: LocationFeatureSpecificationArray
    reviews?: ReviewObject[]
    aggregateRating?: number
  }): CampgroundLeaf {
    let start = undefined
    let end = undefined

    if (seasonStarts && seasonEnds) {
      start = datetime(String(seasonStarts).padStart(4, '0'), 'MMDD')
      end = datetime(String(seasonEnds).padStart(4, '0'), 'MMDD')

      // MMDD is parsed in the same calendar year; seasons like Oct→Mar have end before start until we roll end into the next year.
      if (end.isBefore(start)) {
        end = end.add(1, 'year')
      }

      // If the end of the season is before today, roll season into the next year
      const today = datetime()
      if (end.isBefore(today)) {
        start = start.add(1, 'year')
        end = end.add(1, 'year')
      }
    }

    const filteredReviews = reviews?.filter((review) => Boolean(review.reviewBody) && review.ratingValue > 0)
    const aggregateRatingCount = filteredReviews?.length ?? 0
    const sumRatings = filteredReviews?.reduce((acc, review) => acc + review.ratingValue, 0) ?? 0
    let aggregateRatingValue = 0
    if (typeof aggregateRating === 'number' && !Number.isNaN(aggregateRating)) {
      aggregateRatingValue = aggregateRating
    }
    else if (aggregateRatingCount > 0) {
      aggregateRatingValue = sumRatings / aggregateRatingCount
    }

    return {
      '@id': `${BASE_URL}/${id}`,
      '@type': 'Campground',
      'name': name,
      'telephone': telephone || undefined,
      'url': url || undefined,

      'petsAllowed': petsAllowed,
      'numberOfRooms': numberOfRooms || undefined,
      'priceRange': priceRange || undefined,
      'description': description || undefined,
      'brand': defineBrand({ brand }),

      'image': image || undefined,
      'photo': defineImageObject({ image }),

      'geo': defineGeoCoordinates({ latitude, longitude }),
      'address': definePostalAddress({ streetAddress, city, region, countryCode, postalCode }),
      'containedInPlace': defineContainedInPlace({ city, region, countryCode }),

      'openingHoursSpecification': start && end ? defineOpeningHoursSpecification({ validFrom: start, validThrough: end }) : undefined,

      'amenityFeature': amenityFeature?.map(defineLocationFeatureSpecification) ?? undefined,

      'review': filteredReviews?.map(defineReview) ?? [],
      'aggregateRating': aggregateRatingValue && aggregateRatingCount ? defineAggregateRating({ ratingValue: aggregateRatingValue, reviewCount: aggregateRatingCount }) : undefined,
    } satisfies CampgroundLeaf
  }

  function defineUnitPriceSpecification({ name, price, currency, unitCode }: { name: string, price: number, currency: string, unitCode?: UnitPriceSpecification['unitCode'] }): UnitPriceSpecification {
    return {
      '@type': 'UnitPriceSpecification',
      'name': name,
      'price': price,
      'priceCurrency': currency,
      'unitCode': unitCode ?? undefined,
    }
  }

  function definePriceSpecification({ price, currency, cleaningFee, securityDeposit }: { price: number, currency: string, cleaningFee?: number, securityDeposit?: number }): PriceSpecification {
    const nightly = defineUnitPriceSpecification({ name: 'Nightly rate', price, currency, unitCode: 'DAY' })

    const components: UnitPriceSpecification[] = [nightly]

    if (typeof cleaningFee === 'number') {
      components.push(defineUnitPriceSpecification({ name: 'Cleaning fee', price: cleaningFee, currency }))
    }
    if (typeof securityDeposit === 'number') {
      components.push(defineUnitPriceSpecification({ name: 'Security deposit', price: securityDeposit, currency }))
    }

    if (components.length === 1) {
      return nightly
    }

    const compound: CompoundPriceSpecification = {
      '@type': 'CompoundPriceSpecification',
      // Google’s product/Offer checks expect `priceSpecification.price`; without a
      // top-level price they treat CompoundPriceSpecification as incomplete even
      // when components list nightly + fees (see product-snippet Offer docs).
      'price': price,
      'priceCurrency': currency,
      'priceComponent': components,
    }

    return compound
  }

  function defineVehicle({
    rvName,
    rvType,
    rvRentalType,
    rvMake,
    rvModel,
    rvWeight,
    rvGuests,
    rvYear,
    rvCity,
    rvRegion,
    rvCountry,
    rvFuelType,
    rvDescription,
    rvIsChargeMileage,
    rvIsGenerator,
    rvLength,
    rvSlideouts,
    rvDefaultPrice,
    rvCleaningFee,
    rvSecurityDeposit,
    ownerFirstName,
    images,
    url,
    reviews,
    aggregateRating,
  }: {
    rvName: string
    rvType?: RvExpandedDetails['RVType']
    rvRentalType?: RvExpandedDetails['RentalType']
    rvMake?: RvExpandedDetails['Make']
    rvModel?: RvExpandedDetails['Model']
    rvWeight?: RvExpandedDetails['Weight']
    rvGuests?: RvExpandedDetails['Guests']
    rvYear?: RvExpandedDetails['Year']
    rvCity?: RvExpandedDetails['City']
    rvRegion?: RvExpandedDetails['State']
    rvCountry: COUNTRY_SHORTS
    rvFuelType?: RvExpandedDetails['FuelType']
    rvDescription?: RvExpandedDetails['RVDescription']
    rvIsChargeMileage?: RvExpandedDetails['IsChargeMileage']
    rvIsGenerator?: RvExpandedDetails['IsGenerator']
    rvLength?: RvExpandedDetails['Length']
    rvSlideouts?: RvExpandedDetails['Slideouts']
    rvDefaultPrice?: RvExpandedDetails['DefaultPrice']
    rvCleaningFee?: RvExpandedDetails['CleaningFee']
    rvSecurityDeposit?: RvExpandedDetails['SecurityDeposit']
    ownerFirstName?: RvExpandedDetails['OwnerFirstName']
    images?: string[]
    url?: string
    reviews?: ReviewObject[]
    aggregateRating?: {
      ratingValue: number
      reviewCount: number
    }
  }): Vehicle {
    const brand = rvMake && rvMake !== RV_MANUFACTURERS_OTHER && rvMake !== RV_MANUFACTURERS_NOT_APPLICABLE ? rvMake : undefined

    const currency = lookupCountryCodeToCurrencyCode(rvCountry)
    const filteredReviews = reviews?.filter((review) => Boolean(review.reviewBody) && review.ratingValue > 0)

    const translatedRvType = rvType ? t(`rvType.${rvType}`) : undefined

    return {
      '@type': 'Vehicle',
      'name': escapeDoubleQuotes(rvName) || translatedRvType || 'Unnamed RV',
      'category': rvRentalType ? t(`rvType.${rvRentalType}`) : undefined,
      'model': rvModel || undefined,
      'brand': defineBrand({ brand }),
      'bodyType': translatedRvType,
      'seatingCapacity': rvGuests ?? undefined,
      'modelDate': rvYear ? `${rvYear}-01-01` : undefined,
      'fuelType': rvFuelType ?? undefined,
      'weight': rvWeight
        ? {
            '@type': 'QuantitativeValue',
            'value': rvWeight,
            'unitCode': 'LBS',
          }
        : undefined,
      'vehicleEngine': rvRentalType === 'RVCottage' || rvRentalType === 'TravelTrailer'
        ? {
            '@type': 'EngineSpecification',
            'engineType': t('augmentedSchemaOrg.none'),
          }
        : undefined,
      'vehicleSpecialUsage': 'https://schema.org/RentalVehicleUsage',
      'url': url || undefined,
      'description': escapeDoubleQuotes(rvDescription || ''),
      'image': images,
      'additionalProperty': [
        ...(typeof rvIsChargeMileage === 'boolean'
          ? [
              definePropertyValue({
                name: t('augmentedSchemaOrg.unlimitedMileage'),
                value: rvIsChargeMileage ? t('common.no') : t('common.yes'),
              }),
            ]
          : []),
        ...(typeof rvIsGenerator === 'boolean'
          ? [
              definePropertyValue({
                name: t('augmentedSchemaOrg.generator'),
                value: rvIsGenerator ? t('common.yes') : t('common.no'),
              }),
            ]
          : []),
        ...(typeof rvLength === 'number'
          ? [
              definePropertyValue({
                name: t('augmentedSchemaOrg.length'),
                value: rvLength,
                unitCode: 'FT',
              }),
            ]
          : []),
        ...(typeof rvSlideouts === 'number'
          ? [
              definePropertyValue({
                name: t('augmentedSchemaOrg.slideouts'),
                value: rvSlideouts,
              }),
            ]
          : []),
      ],
      'offers': rvDefaultPrice
        ? {
            '@type': 'Offer',
            'price': rvDefaultPrice,
            'priceCurrency': currency,
            'priceSpecification': definePriceSpecification({ price: rvDefaultPrice, currency, cleaningFee: rvCleaningFee ?? undefined, securityDeposit: rvSecurityDeposit ?? undefined }),
            'offeredBy': {
              '@id': `${BASE_URL}/#identity`,
            },
            'seller': ownerFirstName ? definePerson({ name: ownerFirstName }) : undefined,
            'availableAtOrFrom': {
              '@type': 'Place',
              '@id': `${BASE_URL}/#place-${formatAsSlug([rvCity, rvRegion, rvCountry].filter(Boolean).join('-'))}`,
              'name': [rvCity, rvRegion].filter(Boolean).join(', '),
              'address': definePostalAddress({ city: rvCity ?? undefined, region: rvRegion ?? undefined, countryCode: rvCountry ?? undefined }),
              'containedInPlace': defineContainedInPlace({ city: rvCity ?? undefined, region: rvRegion ?? undefined, countryCode: rvCountry ?? undefined }),
            },
          }
        : undefined,

      'review': filteredReviews?.map(defineReview),
      'aggregateRating': aggregateRating && aggregateRating.reviewCount > 0 && aggregateRating.ratingValue ? defineAggregateRating(aggregateRating) : undefined,
    }
  }

  function rewriteAuthorUrl(url: Nullable<string | undefined>): string | undefined {
    if (!url) {
      return undefined
    }
    return url.replace('https://ghost.rvezy.com:443', BASE_URL).replace(/\/$/, '')
  }

  return {
    defineCampground,
    defineTouristAttraction,
    defineVehicle,
    rewriteAuthorUrl,
  }
}
